import { useState } from "react";
import { projects } from "../data";

/**
 * Pin positions as percentages of the map container (equirectangular projection).
 * Formula: x = (lon + 180) / 360 * 100,  y = (90 - lat) / 180 * 100
 *
 * Dubai, UAE       25.2°N 55.3°E  → x≈65% y≈36%
 * Mumbai, India    19.1°N 72.9°E  → x≈70% y≈39%
 * Singapore         1.3°N 103.8°E → x≈79% y≈49%
 * Berlin, Germany  52.5°N 13.4°E  → x≈54% y≈21%
 */
const PIN_POSITIONS: Record<string, { x: number; y: number }> = {
  "Metro Viaduct Corridor":    { x: 65, y: 36 }, // Dubai, UAE
  "Coastal Highway Expansion": { x: 70, y: 39 }, // Mumbai, India
  "Smart City District":       { x: 79, y: 49 }, // Singapore
  "Renewable Energy Park":     { x: 54, y: 21 }, // Berlin, Germany
};

export function GlobalProjects() {
  const [active, setActive] = useState(0);
  const p = projects[active];

  return (
    <section
      id="projects"
      className="relative overflow-hidden bg-gradient-to-b from-slate-900 to-slate-950 py-24 text-white"
    >
      <div className="mx-auto max-w-7xl px-6">
        {/* Heading */}
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-teal-400">
            Global Projects
          </span>
          <h2 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
            Delivering precision across continents
          </h2>
          <p className="mt-4 text-slate-400">
            Click a location pin on the map to explore deployment case studies
            from our worldwide portfolio.
          </p>
        </div>

        <div className="mt-14 grid gap-10 lg:grid-cols-[1.5fr_1fr] lg:items-start">

          {/* ── World Map ───────────────────────────────────────────────── */}
          <div
            className="relative w-full overflow-hidden rounded-lg border border-white/10"
            style={{ aspectRatio: "2 / 1" }}
          >
            {/* World map image */}
            <img
              src="/images/world-map.webp"
              alt="World map"
              width={1600}
              height={800}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
            />
            {/* Teal tint overlay */}
            <div className="absolute inset-0 bg-slate-950/30" />

            {/* Dot grid texture */}
            <div
              className="absolute inset-0 opacity-30"
              style={{
                backgroundImage:
                  "radial-gradient(circle, rgba(45,212,191,0.2) 1px, transparent 1px)",
                backgroundSize: "20px 20px",
              }}
            />

            {/* Project pins (absolutely positioned by %) */}
            {projects.map((proj, i) => {
              const pos = PIN_POSITIONS[proj.name] ?? { x: 50, y: 50 };
              const isActive = active === i;
              return (
                <button
                  key={proj.name}
                  onClick={() => setActive(i)}
                  title={`${proj.name} — ${proj.location}`}
                  className="absolute -translate-x-1/2 -translate-y-1/2 focus:outline-none"
                  style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                >
                  {/* Pulse ring */}
                  {isActive && (
                    <span className="absolute inset-0 -m-3 animate-ping rounded-full bg-teal-400 opacity-30" />
                  )}
                  {/* Outer glow ring */}
                  <span
                    className={`absolute rounded-full transition-all duration-300 ${
                      isActive
                        ? "-inset-3 bg-teal-400/20 ring-2 ring-teal-400/50"
                        : "-inset-2 bg-transparent"
                    }`}
                  />
                  {/* Pin dot */}
                  <span
                    className={`relative block rounded-full transition-all duration-300 ${
                      isActive
                        ? "h-4 w-4 bg-teal-400 shadow-[0_0_12px_4px_rgba(45,212,191,0.6)]"
                        : "h-3 w-3 bg-teal-500 hover:bg-teal-400 hover:shadow-[0_0_8px_2px_rgba(45,212,191,0.5)]"
                    }`}
                  />
                  {/* Location label */}
                  {isActive && (
                    <span className="absolute bottom-full left-1/2 mb-2 -translate-x-1/2 whitespace-nowrap rounded-sm border border-teal-400/30 bg-slate-900/90 px-2.5 py-1 text-[0.65rem] font-bold text-teal-300 backdrop-blur">
                      {proj.location}
                    </span>
                  )}
                </button>
              );
            })}

            {/* Legend */}
            <div className="absolute bottom-3 left-3 flex items-center gap-2 rounded-md border border-white/10 bg-slate-900/70 px-3 py-1.5 backdrop-blur">
              <span className="h-2 w-2 rounded-full bg-teal-400" />
              <span className="text-[0.6rem] font-semibold uppercase tracking-widest text-slate-400">
                Project Location
              </span>
            </div>
          </div>

          {/* ── Case Study Card ──────────────────────────────────────────── */}
          <div className="overflow-hidden rounded-lg border border-white/10 bg-slate-900/70 backdrop-blur">
            <div className="relative aspect-[16/9] overflow-hidden">
              <img
                key={p.name}
                src={p.image}
                alt={p.name}
                width={1200}
                height={627}
                loading="lazy"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />
              <span className="absolute bottom-4 left-5 inline-flex items-center gap-2 text-sm font-medium text-teal-300">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 21s-7-5.5-7-11a7 7 0 1 1 14 0c0 5.5-7 11-7 11Z" />
                  <circle cx="12" cy="10" r="2.5" />
                </svg>
                {p.location}
              </span>
            </div>

            <div className="p-6">
              <h3 className="text-lg font-semibold">{p.name}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{p.text}</p>

              {/* Dot indicators */}
              <div className="mt-5 flex gap-2">
                {projects.map((proj, i) => (
                  <button
                    key={proj.name}
                    onClick={() => setActive(i)}
                    className={`h-2 rounded-full transition-all ${
                      active === i ? "w-8 bg-teal-400" : "w-2 bg-white/20 hover:bg-white/40"
                    }`}
                    aria-label={proj.name}
                  />
                ))}
              </div>

              {/* Project list */}
              <div className="mt-4 space-y-1 border-t border-white/10 pt-4">
                {projects.map((proj, i) => (
                  <button
                    key={proj.name}
                    onClick={() => setActive(i)}
                    className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition ${
                      active === i
                        ? "bg-teal-600/20 text-teal-300"
                        : "text-slate-400 hover:bg-white/5 hover:text-white"
                    }`}
                  >
                    <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${active === i ? "bg-teal-400" : "bg-slate-600"}`} />
                    <span className="font-medium">{proj.name}</span>
                    <span className="ml-auto shrink-0 text-xs text-slate-500">{proj.location}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Client logos */}
        <div className="mt-16 border-t border-white/10 pt-10">
          <p className="text-center text-xs uppercase tracking-[0.3em] text-slate-500">
            Trusted by leading builders worldwide
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-12 gap-y-4 text-lg font-semibold text-slate-500">
            {["ConstructaGroup", "Veridian", "NordBuild", "ApexInfra", "Stratos", "MetroWorks"].map((c) => (
              <span key={c} className="transition hover:text-teal-300">{c}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
